import React, { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { theme } from 'antd';

const ThemeContext = createContext();

const getSystemDark = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches;

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem('theme') || 'system';
    } catch {
      return 'system';
    }
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (themeMode === 'dark') return true;
    if (themeMode === 'light') return false;
    return getSystemDark();
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (themeMode === 'system') {
        setIsDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  useEffect(() => {
    try {
      localStorage.setItem('theme', themeMode);
    } catch {
      // Appearance changes still apply when storage is unavailable.
    }

    const dark =
        themeMode === 'dark' ||
        (themeMode === 'system' && getSystemDark());

    setIsDarkMode(dark);

    document.documentElement.setAttribute(
      'data-theme',
      dark ? 'dark' : 'light'
    );

    if (dark) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode(isDarkMode ? 'light' : 'dark');
  };

  const resetToSystemTheme = () => {
    setThemeMode('system');
  };

  const algorithm = isDarkMode
    ? theme.darkAlgorithm
    : theme.defaultAlgorithm;

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        themeMode,
        toggleTheme,
        resetToSystemTheme,
        algorithm
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = { children: PropTypes.node.isRequired };

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
